import { useCallback, useMemo } from 'react';
import { Node, useEditor } from '@craftjs/core';
import { useContextSelector } from 'use-context-selector';
import { ReactComponent as UndoSvg } from '../../../../assets/img/formBuilder/undo.svg';
import { AppButton } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { FaqEditorContext } from '../../faqEditor.context';
import Style from './viewport.module.css';
import { useTranslation } from 'react-i18next';
import { FilesQuery, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { downloadFile } from 'src/helpers/file-helper';
import { isNotEmptyImageBlock } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { AppButtonWithMenu } from 'uikit/buttons/buttonWithMenu/buttonWithMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';

const USE_FILE_ID_AS_NAME = true;

export const Header = () => {
  const { t: devLocalizer } = useTranslation('dev');
  const { t } = useTranslation();
  const study = useStudy();
  const { hasStudyClaim } = useHasStudyClaims();
  const { enabled, canUndo, canRedo, actions, query } = useEditor((state, q) => ({
    enabled: state.options.enabled,
    canUndo: q.history.canUndo(),
    canRedo: q.history.canRedo(),
  }));

  const onSave = useContextSelector(FaqEditorContext, (x) => x.onSave);
  const setDesignerMode = useContextSelector(FaqEditorContext, (x) => x.setDesignerMode);

  const fileIds = useMemo(() => getFileIdsFromLayout(query.getNodes()), [query]);
  const downloadImagesHandler = useCallback(async () => {
    if (!fileIds.length) return;

    const images = await FilesQuery.Client.getFiles(fileIds, USE_FILE_ID_AS_NAME);
    downloadFile(images);
  }, [fileIds]);

  const options = useMemo(
    () =>
      [
        {
          key: 1,
          text: devLocalizer('StudySettingsPage.FormBuilder.copy_to_clipboard'),
          action: () => {
            const json = query.serialize();
            navigator.clipboard.writeText(json);
          },
        },
        {
          key: 2,
          text: devLocalizer('StudySettingsPage.FormBuilder.DownloadImages'),
          action: downloadImagesHandler,
          disabled: !fileIds.length,
        },
      ] satisfies DropDownMenuOption[],
    [devLocalizer, downloadImagesHandler, fileIds.length, query],
  );

  return (
    <div className={Style.header}>
      <div className={Style.headerContainer}>
        {enabled && (
          <div className={Style.undoRedoContainer}>
            <Tooltip text={t('FaqEditor.Header.Undo')}>
              <AppButton
                disabled={!canUndo}
                Icon={UndoSvg}
                onClick={actions.history.undo}
                variant={'icon-link'}
                colorSchema={'primary'}
              />
            </Tooltip>
            <Tooltip text={t('FaqEditor.Header.Redo')}>
              <AppButton
                disabled={!canRedo}
                Icon={UndoSvg}
                onClick={actions.history.redo}
                variant={'icon-link'}
                colorSchema={'primary'}
                iconClassName={Style.redoButton}
              />
            </Tooltip>
          </div>
        )}
        <div className={Style.rightButtonContainer}>
          {enabled && (
            <AppButtonWithMenu
              options={options}
              buttonText={t('Common_Save')}
              onClick={() => {
                onSave?.(query.getSerializedNodes());
                setDesignerMode?.(false);
              }}
            />
          )}
          {!study?.closingApprovedAt && hasStudyClaim(StudyRoleClaimValue.StudyConfigure) && (
            <AppButton
              variant={'icon-link'}
              colorSchema={'primary'}
              text={t(enabled ? 'FaqEditor.Header.Preview' : 'FaqEditor.Header.Edit')}
              onClick={() => setDesignerMode?.(!enabled)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const getFileIdsFromLayout = (nodes: Record<string, Node>) => {
  return Object.values(nodes)
    .filter((x) => isNotEmptyImageBlock(x.data.props))
    .map((x) => x.data.props.fileId);
};
